import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Trophy, CheckCircle2, Share2, Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';

interface StreakCongratulationModalProps {
  isOpen: boolean;
  onClose: () => void;
  streak: number;
}

const MOTIVATIONAL_PHRASES_BY_LNG: Record<string, string[]> = {
  pt: [
    "Incrível! Sua dedicação está dando frutos. 📚",
    "Mais um dia de progresso! Você está no caminho certo. 🌟",
    "Sua constância é inspiradora! Vamos para o próximo nível. 🚀",
    "Cada dia conta, e hoje você deu um show! 🧠",
    "Foco total! Sua jornada de aprendizado continua impecável. ✨"
  ],
  en: [
    "Incredible! Your dedication is paying off. 📚",
    "Another day of progress! You're on the right track. 🌟",
    "Your consistency is inspiring! Let's go to the next level. 🚀",
    "Every day counts, and today you put on a show! 🧠",
    "Total focus! Your learning journey remains flawless. ✨"
  ],
  es: [
    "¡Increíble! Tu dedicación está dando frutos. 📚",
    "¡Otro día de progreso! Vas por el camino correcto. 🌟",
    "¡Tu constancia es inspiradora! Vamos al siguiente nivel. 🚀",
    "¡Cada dia cuenta, y hoy diste un gran espectáculo! 🧠",
    "¡Foco total! Tu viaje de aprendizaje sigue siendo impecable. ✨"
  ],
  fr: [
    "Incroyable ! Votre dévouement porte ses fruits. 📚",
    "Un autre jour de progrès ! Vous êtes sur la bonne voie. 🌟",
    "Votre régularité est inspirante ! Passons au niveau supérieur. 🚀",
    "Chaque jour compte, et aujourd'hui vous avez assuré ! 🧠",
    "Focus total ! Votre parcours d'apprentissage reste impeccable. ✨"
  ],
  de: [
    "Unglaublich! Dein Einsatz zahlt sich aus. 📚",
    "Ein weiterer Tag des Fortschritts! Du bist auf dem richtigen Weg. 🌟",
    "Deine Beständigkeit ist inspirierend! Lass uns aufs nächste Level gehen. 🚀",
    "Jeder Tag zählt, und heute hast du eine tolle Leistung gezeigt! 🧠",
    "Voller Fokus! Deine Lernreise bleibt makellos. ✨"
  ],
  it: [
    "Incredibile! La tua dedizione sta dando i suoi frutti. 📚",
    "Un altro giorno di progressi! Sei sulla strada giusta. 🌟",
    "La tua costanza è stimolante! Passiamo al livello successivo. 🚀",
    "Ogni giorno conta, e oggi hai fatto un figurone! 🧠",
    "Focus totale! Il tuo percorso di apprendimento rimane impeccabile. ✨"
  ],
  hu: [
    "Hihetetlen! A dedikációd kifizetődik. 📚",
    "Újabb nap a fejlődés útján! Jó úton jársz. 🌟",
    "A következetességed inspiráló! Lépjünk a következő szintre. 🚀",
    "Minden nap számít, és ma kitettél magadért! 🧠",
    "Teljes fókusz! A tanulási utad továbbra is hibátlan. ✨"
  ],
  ja: [
    "素晴らしい！あなたの努力が実を結んでいます。 📚",
    "また一歩前進です！あなたは正しい道を進んでいます。 🌟",
    "あなたの継続力は素晴らしいです！次のレベルへ進みましょう。 🚀",
    "毎日が大切です。今日は素晴らしい成果を出しました！ 🧠",
    "集中力抜群！あなたの学習ジャーニーは完璧です。 ✨"
  ],
  zh: [
    "不可思议！你的付出正在取得回报。 📚",
    "又是进步的一天！你走在正确的轨道上。 🌟",
    "你的坚持令人鼓舞！让我们进入下一个阶段。 🚀",
    "每一天都很重要，今天你表现得太棒了！ 🧠",
    "全神贯注！你的学习之旅依然完美无瑕。 ✨"
  ],
  ru: [
    "Невероятно! Ваше усердие приносит плоды. 📚",
    "Еще один день прогресса! Вы на правильном пути. 🌟",
    "Ваша стабильность вдохновляет! Перейдем на следующий уровень. 🚀",
    "Каждый день на счету, и сегодня вы показали класс! 🧠",
    "Полная концентрация! Ваш учебный путь остается безупречным. ✨"
  ]
};

const STREAK_TRANSLATIONS: Record<string, { title: string; day: string; days: string; confirm: string; share: string; download: string; shareApp: string }> = {
  pt: { title: "Incrível!", day: "Dia", days: "Dias", confirm: "Confirmar", share: "Compartilhar", download: "Baixar Imagem", shareApp: "Compartilhar em outro app" },
  en: { title: "Incredible!", day: "Day", days: "Days", confirm: "Confirm", share: "Share", download: "Download Image", shareApp: "Share to another app" },
  es: { title: "¡Increíble!", day: "Día", days: "Días", confirm: "Confirmar", share: "Compartir", download: "Descargar Imagen", shareApp: "Compartir en otra app" },
  fr: { title: "Incroyable !", day: "Jour", days: "Jours", confirm: "Confirmer", share: "Partager", download: "Télécharger l'image", shareApp: "Partager sur una autre app" },
  de: { title: "Unglaublich!", day: "Tag", days: "Tage", confirm: "Bestätigen", share: "Teilen", download: "Bild herunterladen", shareApp: "In anderer App teilen" },
  it: { title: "Incredibile!", day: "Giorno", days: "Giorni", confirm: "Conferma", share: "Condividi", download: "Scarica Immagine", shareApp: "Condividi su un'altra app" },
  hu: { title: "Hihetetlen!", day: "Nap", days: "Nap", confirm: "Megerősítés", share: "Megosztás", download: "Kép letöltése", shareApp: "Megosztás másik alkalmazásban" },
  ja: { title: "素晴らしい！", day: "日", days: "日", confirm: "確認", share: "共有", download: "画像をダウンロード", shareApp: "他のアプリで共有" },
  zh: { title: "不可思议！", day: "天", days: "天", confirm: "确认", share: "分享", download: "下载图片", shareApp: "分享到其他应用" },
  ru: { title: "Невероятно!", day: "День", days: "Дней", confirm: "Подтвердить", share: "Поделиться", download: "Скачать изображение", shareApp: "Поделиться в другом приложении" }
};

const StreakCongratulationModal: React.FC<StreakCongratulationModalProps> = ({ isOpen, onClose, streak }) => {
  const { i18n } = useTranslation();
  const { isDarkMode, accentColor } = useTheme();
  const [phrase, setPhrase] = useState("");
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const langCode = (i18n.language || 'pt').split('-')[0].toLowerCase();
  const localT = STREAK_TRANSLATIONS[langCode] || STREAK_TRANSLATIONS.pt;

  useEffect(() => {
    if (isOpen) {
      const phrases = MOTIVATIONAL_PHRASES_BY_LNG[langCode] || MOTIVATIONAL_PHRASES_BY_LNG.pt;
      const randomPhrase = phrases[Math.floor(Math.random() * phrases.length)];
      setPhrase(randomPhrase);
      setShowShareOptions(false);
    }
  }, [isOpen, langCode]);

  const generateBlob = async (): Promise<Blob | null> => {
    if (!cardRef.current) return null;
    try {
      const { toBlob } = await import('html-to-image');
      const blob = await toBlob(cardRef.current, { quality: 0.95, pixelRatio: 2 });
      return blob;
    } catch (err) {
      console.error("Failed to generate streak image:", err);
      return null;
    }
  };

  const handleDownloadImage = async () => {
    setIsGenerating(true);
    try {
      const blob = await generateBlob();
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `libritui_streak_${streak}_dias.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShareNative = async () => {
    setIsGenerating(true);
    try {
      const blob = await generateBlob();
      const shareText = `🔥 ${streak} ${streak === 1 ? localT.day : localT.days} de Sequência no Libritui! 🎉`;
      if (blob && navigator.share) {
        const file = new File([blob], `streak_${streak}_dias.png`, { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: 'Minha Streak no Libritui',
            text: shareText,
            files: [file]
          });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({
          title: 'Minha Streak no Libritui',
          text: shareText
        });
      } else {
        await handleDownloadImage();
      }
    } catch (err) {
      console.warn("Share cancelled or failed:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            ref={cardRef}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className={`
              relative w-full max-w-sm p-8 rounded-[28px] shadow-[0_20px_70px_rgba(0,0,0,0.5)] border overflow-hidden
              ${isDarkMode ? 'bg-surface border-black/[0.06] dark:border-white/[0.06]' : 'bg-surface border-black/[0.06]'}
            `}
          >
            {/* Background Sparkles */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
              <div className="absolute top-10 left-10 text-warn/20 animate-pulse">
                <Sparkles size={40} />
              </div>
              <div className="absolute bottom-10 right-10 text-primary/20 animate-pulse">
                <Sparkles size={60} />
              </div>
            </div>

            <div className="flex flex-col items-center text-center space-y-6 relative z-10">
              <div className={`
                w-24 h-24 rounded-[24px] flex items-center justify-center
                ${isDarkMode ? 'bg-warn/20 text-warn' : 'bg-tint-butter text-warn'}
                animate-bounce
              `}>
                <Trophy size={48} />
              </div>

              <div className="space-y-2">
                <h2 className={`text-3xl font-medium tracking-[-0.03em] ${isDarkMode ? 'text-ink' : 'text-muted/60'}`}>
                  {localT.title}
                </h2>
                <p className={`text-sm font-medium px-4 ${isDarkMode ? 'text-muted' : 'text-muted'}`}>
                  {phrase}
                </p>
              </div>

              <div className={`
                px-6 py-3 rounded-2xl flex items-center gap-3
                ${isDarkMode ? 'bg-tint-stone' : 'bg-tint-stone/60'}
              `}>
                <div className="text-warn">
                  <Sparkles size={20} fill="currentColor" />
                </div>
                <span className={`text-2xl font-medium ${isDarkMode ? 'text-ink' : 'text-muted/60'}`}>
                  {streak} {streak === 1 ? localT.day : localT.days}
                </span>
              </div>

              <div className="w-full space-y-2">
                <button
                  onClick={onClose}
                  className={`
                    w-full py-4 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 transition-all active:scale-95
                    text-ink
                  `}
                  style={{ backgroundColor: accentColor }}
                >
                  <CheckCircle2 size={18} />
                  {localT.confirm}
                </button>

                {/* Discrete pastel Share button */}
                <button
                  onClick={() => setShowShareOptions(prev => !prev)}
                  className={`
                    w-full py-3 rounded-2xl font-normal text-xs uppercase font-mono tracking-[0.14em] flex items-center justify-center gap-2 transition-all active:scale-95 border
                    ${isDarkMode 
                      ? 'bg-warn/15 text-warn border-warn/30 hover:bg-warn/25' 
                      : 'bg-tint-butter text-warn border-tint-butter hover:bg-tint-butter'
                    }
                  `}
                >
                  <Share2 size={16} />
                  {localT.share}
                </button>

                {/* Share Options Popup Sheet */}
                {showShareOptions && (
                  <div className="w-full pt-3 mt-3 border-t border-black/[0.06] dark:border-white/[0.06] space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <button
                      onClick={handleDownloadImage}
                      disabled={isGenerating}
                      className={`
                        w-full py-2.5 px-4 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all active:scale-95
                        ${isDarkMode ? 'bg-tint-stone text-ink hover:bg-tint-stone' : 'bg-tint-stone/60 text-muted/60 hover:bg-tint-stone/60'}
                      `}
                    >
                      {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                      {localT.download}
                    </button>
                    <button
                      onClick={handleShareNative}
                      disabled={isGenerating}
                      className={`
                        w-full py-2.5 px-4 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all active:scale-95
                        ${isDarkMode ? 'bg-tint-stone text-ink hover:bg-tint-stone' : 'bg-tint-stone/60 text-muted/60 hover:bg-tint-stone/60'}
                      `}
                    >
                      {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                      {localT.shareApp}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StreakCongratulationModal;
